import React, { memo, useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLongLoad } from 'reactApp/hooks/useLongLoad';
import { PromoSelectors } from 'reactApp/modules/promo/promo.selectors';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';

import { SearchTooltip } from '../SearchTooltip/SearchTooltip';
import { SearchTooltipTypes } from '../SearchTooltip/SearchTooltip.data';
import { getSearchFilterTooltipType, getSearchTooltipType } from '../SearchTooltip/SearchTooltip.helpers';
import { useTooltipState } from '../SearchTooltip/SearchTooltip.hooks';
import { CategoryFilters } from './CategoryFilters/CategoryFilters';
import { ClearButton } from './ClearButton/ClearButton';
import { useWebSearchContent } from './hooks/useWebSearchContent';
import { SearchButton } from './SearchButton/SearchButton';
import { SearchInput } from './SearchInput/SearchInput';
import { SearchSuggests } from './SearchSuggests/SearchSuggests';
import { TransitionWrapper } from './TransitionWrapper/TransitionWrapper';
import { TypeSelectButton } from './TypeSelectButton/TypeSelectButton';

const inputBorderRadius = {
    '--vkui--size_border_radius--regular': '8px 0 0 8px',
} as React.CSSProperties;

interface IProps {
    dropdownAvailable: boolean;
    toggleSearch: (state: boolean) => void;
    isExpanded: boolean;
    onlyExpanded?: boolean;
    storage: EStorageType;
}

export const WebSearch = memo(({ dropdownAvailable, toggleSearch, isExpanded, storage, onlyExpanded = false }: IProps) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const categoryFilersRef = useRef<HTMLButtonElement>(null);
    const searchRootRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // tempexp_SRCH-70119-start
    const [isSearchFullyExpanded, setSearchFullyExpanded] = useState(isExpanded || onlyExpanded);

    const searchTooltipType = getSearchTooltipType();
    const searchFilterTooltipType = getSearchFilterTooltipType();
    const searchTooltipPromo = useSelector(PromoSelectors.getPromo(EPromoType.searchTooltip));
    const searchFilterTooltipPromo = useSelector(PromoSelectors.getPromo(EPromoType.searchFilterTooltipPromo));

    const { isTooltipVisible: isSearchTooltipVisible, onTooltipClose: onSearchTooltipClose } = useTooltipState({
        visibilityValue: !!(
            searchTooltipPromo &&
            searchTooltipType &&
            searchInputRef?.current &&
            !isSearchFullyExpanded &&
            !(isExpanded || onlyExpanded)
        ),
        tooltipType: searchTooltipType,
        promo: searchTooltipPromo,
    });
    const { isTooltipVisible: isSearchFilterTooltipVisible, onTooltipClose: onSearchFilterTooltipClose } = useTooltipState({
        visibilityValue: !!(
            searchFilterTooltipPromo &&
            searchFilterTooltipType &&
            isSearchFullyExpanded &&
            (isExpanded || onlyExpanded) &&
            categoryFilersRef
        ),
        tooltipType: searchFilterTooltipType,
        promo: searchFilterTooltipPromo,
    });

    const onSearchStateChange = ({ type, payload }: { type: string; payload?: any }) => {
        if (type === 'setActive') {
            onSearchTooltipClose();
        }
        if (type === 'toggleFilters' || type === 'setValue' || (type === 'setFocus' && payload)) {
            onSearchFilterTooltipClose();
        }
    };

    const onTransitionEntered = useCallback(() => {
        setActiveInput();
        setSearchFullyExpanded(true);
    }, []);

    const onTransitionExited = useCallback(() => setSearchFullyExpanded(false), []);
    // tempexp_SRCH-70119-end

    const {
        isFocus,
        isOpen,
        value,
        searchOption,
        placeholder,
        onSearchClick,
        onInputClick,
        onClickAway,
        onInputChange,
        onInputClear,
        onInputBlur,
        onKeyUp,
        onToggleFilters,
        searchValue,
        setActiveInput,
        chooseSearchType,
        removeSearchType,
        historyItems,
    } = useWebSearchContent({ toggleSearch, isExpanded, onlyExpanded, storage, onSearchStateChange });
    const { longLoad } = useLongLoad();

    return (
        <TransitionWrapper ref={rootRef} isExpanded={isExpanded} onEntered={onTransitionEntered} onExited={onTransitionExited}>
            <SearchInput
                placeholder={placeholder}
                onClick={onInputClick}
                onChange={onInputChange}
                onBlur={onInputBlur}
                value={value}
                onKeyUp={onKeyUp}
                style={isExpanded ? inputBorderRadius : undefined} // TODO убрать инлайн-стиль после перехода на 5-ю версию VKUI
                getRef={searchInputRef}
                getRootRef={searchRootRef}
                after={isExpanded && <ClearButton onClick={onInputClear} />}
            />
            {isExpanded && (
                <>
                    {dropdownAvailable && (
                        <TypeSelectButton searchOption={searchOption} onClick={onToggleFilters} ref={categoryFilersRef} isOpen={isOpen} />
                    )}
                    <SearchButton loading={longLoad} onClick={onSearchClick} />
                    {!isOpen && isFocus && historyItems.length > 0 && (
                        <SearchSuggests
                            rootRef={rootRef}
                            targetRef={searchRootRef}
                            items={historyItems}
                            onClick={searchValue}
                            onClickAway={onClickAway}
                        />
                    )}
                    {dropdownAvailable && isOpen && (
                        <CategoryFilters
                            searchOption={searchOption}
                            targetRef={categoryFilersRef}
                            rootRef={rootRef}
                            onSelect={chooseSearchType}
                            onReset={removeSearchType}
                            onClickAway={onClickAway}
                        />
                    )}
                </>
            )}
            {isSearchTooltipVisible && searchInputRef?.current && (
                <SearchTooltip targetRef={searchInputRef} type={searchTooltipType as SearchTooltipTypes} onClose={onSearchTooltipClose} />
            )}
            {isSearchFilterTooltipVisible && isSearchFullyExpanded && categoryFilersRef?.current && (
                <SearchTooltip
                    targetRef={categoryFilersRef}
                    type={searchFilterTooltipType as SearchTooltipTypes}
                    onClose={onSearchFilterTooltipClose}
                />
            )}
        </TransitionWrapper>
    );
});

WebSearch.displayName = 'WebSearch';
